import React from 'react';
import { ExternalLink, Share2 } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon, SocialNetworkIcon } from './social-icons';
import { useSocialPosts } from '../../hooks/social/use-social-posts';
import { useSocialNetworks } from '../../hooks/use-social-networks';

export const SocialFeed: React.FC = () => {
  const { posts, loading, error } = useSocialPosts();
  const { networks: socialNetworks } = useSocialNetworks();

  const activeSocialNetworks = (socialNetworks && socialNetworks.length > 0)
    ? socialNetworks.filter((sn) => sn.isActive).sort((a, b) => a.displayOrder - b.displayOrder)
    : [];

  if (loading) {
    return (
      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="h-6 w-48 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
          <div className="h-4 w-28 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-80 rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 animate-pulse"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  const getPlatformMeta = (platform: string) => {
    switch (platform.toUpperCase()) {
      case 'LINKEDIN':
        return {
          name: 'LinkedIn',
          icon: <LinkedInIcon className="w-4 h-4 text-[#0A66C2]" />,
          badgeClass: 'bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/20',
          btnHover: 'hover:border-[#0A66C2] hover:text-[#0A66C2]',
        };
      case 'INSTAGRAM':
        return {
          name: 'Instagram',
          icon: <InstagramIcon className="w-4 h-4 text-[#E1306C]" />,
          badgeClass: 'bg-[#E1306C]/10 text-[#E1306C] border-[#E1306C]/20',
          btnHover: 'hover:border-[#E1306C] hover:text-[#E1306C]',
        };
      case 'FACEBOOK':
        return {
          name: 'Facebook',
          icon: <FacebookIcon className="w-4 h-4 text-[#1877F2]" />,
          badgeClass: 'bg-[#1877F2]/10 text-[#1877F2] border-[#1877F2]/20',
          btnHover: 'hover:border-[#1877F2] hover:text-[#1877F2]',
        };
      default:
        return {
          name: 'Social',
          icon: <Share2 className="w-4 h-4 text-brand-primary" />,
          badgeClass: 'bg-brand-50 text-brand-primary border-brand-200',
          btnHover: 'hover:border-brand-primary hover:text-brand-primary',
        };
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('es-CL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 w-full">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 sm:mb-8 gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-50 dark:bg-brand-950/40 text-brand-primary border border-brand-200 dark:border-brand-900/50 mb-2">
            <Share2 className="w-3.5 h-3.5" />
            <span>Comunidad & Actualidad</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white tracking-tight">
            Últimas publicaciones en redes sociales
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Conoce casos de éxito, lanzamientos técnicos de hardware y eventos en directo.
          </p>
        </div>

        {activeSocialNetworks.length > 0 && (
          <div className="flex items-center gap-3">
            {activeSocialNetworks.map((sn) => (
              <a
                key={sn.id}
                href={sn.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:text-brand-primary hover:border-brand-primary transition-colors"
                title={sn.name}
              >
                <SocialNetworkIcon typeOrIcon={sn.type || sn.name} className="w-4 h-4" />
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post) => {
          const meta = getPlatformMeta(post.platform);
          return (
            <article
              key={post.id}
              className="group flex flex-col justify-between rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              {post.mediaUrl && (
                <div className="relative h-48 w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                  <img
                    src={post.mediaUrl}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-md border ${meta.badgeClass}`}
                    >
                      {meta.icon}
                      <span>{meta.name}</span>
                    </span>
                  </div>
                </div>
              )}

              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  {!post.mediaUrl && (
                    <div className="mb-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.badgeClass}`}
                      >
                        {meta.icon}
                        <span>{meta.name}</span>
                      </span>
                    </div>
                  )}

                  <h3 className="font-bold text-base text-neutral-900 dark:text-white line-clamp-2 group-hover:text-brand-primary transition-colors">
                    {post.title}
                  </h3>

                  <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2 line-clamp-3 leading-relaxed">
                    {post.content}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800/80 flex items-center justify-between text-xs">
                  <time className="text-neutral-400 font-medium">
                    {formatDate(post.publishedAt)}
                  </time>

                  <a
                    href={post.postUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 font-semibold text-neutral-700 dark:text-neutral-300 ${meta.btnHover} transition-colors`}
                  >
                    <span>Ver publicación</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
